import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { Pencil, Save } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, ConfirmDialog } from '../components';
import { Layout } from '../components/Layout';
import { DynamicFormRenderer } from '../components/FormBuilder';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { validateField } from '../utils/fieldValidation';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FormInfoBanner = styled.div`
  background: ${props => props.theme.isDark
    ? `linear-gradient(135deg, ${props.theme.colors.tint}, ${props.theme.colors.tint}80)`
    : 'linear-gradient(304deg, #444aff -19%, #232562, #0f1229 100%)'};
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const FormIconLarge = styled.div`
  font-size: 48px;
`;

const InfoBox = styled.div`
  background-color: ${props => props.theme.colors.tint}15;
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

export const SubformSubmissionPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, sessionGuid, fieldId, instanceIndex } = useParams();

  // Get data passed via navigation state
  const {
    subformSchema = [],
    instanceData: initialInstanceData = {},
    fieldLabel = 'Form Instance',
    minInstances = 0,
    maxInstances = 10,
    currentInstanceCount = 0,
    returnUrl = `/forms/${formId}/submissions/create`
  } = location.state || {};

  const [formData, setFormData] = useState(initialInstanceData);
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, onConfirm: null });

  // Warn about unsaved changes
  useUnsavedChangesWarning(isDirty);

  // Track changes to mark form as dirty
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      setIsDirty(true);
    }
  }, [formData]);

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    subformSchema.forEach(field => {
      const value = formData[field.id];

      // Check required fields
      if (field.props?.required) {
        if (value === undefined || value === null || value === '') {
          newErrors[field.id] = `${field.props.label} is required`;
        }
      }

      // Validate scanner fields with validation rules
      if (field.type === 'qr_scanner' || field.type === 'barcode_scanner') {
        if (value && field.props?.validationRules) {
          const validation = validateField(value, field.props.validationRules);
          if (!validation.isValid) {
            newErrors[field.id] = validation.errors.join('; ');
          }
        }
      }
    });

    return newErrors;
  };

  const handleSave = (saveAndNew = false) => {
    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Object.values(validationErrors).forEach(error => toast.error(error));
      return;
    }

    // Navigate back with updated data
    navigate(returnUrl, {
      state: {
        subformUpdate: {
          fieldId,
          instanceIndex: instanceIndex !== undefined ? parseInt(instanceIndex) : null,
          instanceData: formData
        }
      }
    });

    setIsDirty(false);

    if (saveAndNew) {
      // Reset form for new instance
      setFormData({});
      setErrors({});
      toast.success('Instance saved. Add another.');
    } else {
      toast.success('Instance saved successfully');
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmState({
        isOpen: true,
        onConfirm: () => {
          setConfirmState(s => ({ ...s, isOpen: false }));
          navigate(returnUrl, { state: {} });
        },
      });
      return;
    }

    navigate(returnUrl, { state: {} });
  };

  const handleFormChange = (newFormData) => {
    setFormData(newFormData);
    // Clear errors for changed fields
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      Object.keys(newFormData).forEach(key => {
        if (newFormData[key] !== formData[key]) {
          delete newErrors[key];
        }
      });
      setErrors(newErrors);
    }
  };

  const isEditMode = instanceIndex !== undefined;
  const instanceNumber = isEditMode ? parseInt(instanceIndex) + 1 : 'New';

  // Derive a meaningful label from the first non-empty text field
  const getInstanceTitle = () => {
    if (!isEditMode) return `${fieldLabel} (New)`;
    const displayFields = subformSchema.filter(f => f.type !== 'header' && f.type !== 'divider');
    for (const field of displayFields) {
      const value = initialInstanceData[field.id];
      if (value !== null && value !== undefined && value !== '') {
        const str = String(value).trim();
        if (str.length > 0 && str !== 'false' && !str.startsWith('[object')) {
          const label = str.length > 50 ? `${str.substring(0, 50)}...` : str;
          return `${fieldLabel} #${instanceNumber} - ${label}`;
        }
      }
    }
    return `${fieldLabel} #${instanceNumber}`;
  };

  const instanceTitle = getInstanceTitle();

  return (
    <Layout>
      <Helmet>
        <title>{isEditMode ? `Edit ${fieldLabel}` : `Add ${fieldLabel}`} - aQRate</title>
      </Helmet>

      <PageContainer>
        {/* Info Banner */}
        <FormInfoBanner theme={theme}>
          <FormIconLarge><Pencil size={28} /></FormIconLarge>
          <div>
            <ThemedText type="title" style={{ color: 'white' }}>
              {instanceTitle}
            </ThemedText>
            <ThemedText style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {isEditMode ? 'Edit Instance' : 'Add New Instance'}
            </ThemedText>
          </div>
        </FormInfoBanner>

        {/* Instance Counter */}
        <InfoBox theme={theme}>
          <ThemedText>
            {currentInstanceCount} of {maxInstances} instances ({minInstances} minimum required)
          </ThemedText>
        </InfoBox>

        {/* Form Card */}
        <FormCard theme={theme}>
          {subformSchema.length > 0 ? (
            <DynamicFormRenderer
              schema={subformSchema}
              formData={formData}
              onChange={handleFormChange}
              errors={errors}
            />
          ) : (
            <ThemedText style={{ color: theme.colors.muted, textAlign: 'center', padding: theme.spacing.xl }}>
              No fields configured for this form.
            </ThemedText>
          )}

          <ButtonGroup theme={theme}>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => handleSave(false)}>
              <Save size={14} /> Save & Close
            </Button>
            <Button variant="primary" onClick={() => handleSave(true)}>
              <Save size={14} /> Save & Add Another
            </Button>
          </ButtonGroup>
        </FormCard>
      </PageContainer>

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState(s => ({ ...s, isOpen: false }))}
      />
    </Layout>
  );
};
